import React from 'react';
import { Button, Table } from '@odin-labs/components';
import { ChangeType } from 'apollo/generated/client-operations';
import { PlusIcon } from 'components/icons';
import { NewHeader } from 'components/header/NewHeader';
import { useModalChangeActions } from 'utils/useModalChangeActions';
import { languageOptions } from 'containers/jobsiteConfiguration/helpers/utils';
import {
  DeleteDocumentFileModal,
  EditDocumentFileModal,
  EditableDocumentFile,
  AddDocumentFileModal,
} from './modals';
import { DocumentFilesProps } from './types';
import { getColumns, DocumentFileColumn } from './DocumentFiles.tables';

const classes = {
  error: 'odin-mt-2 odin-text-sm odin-text-odin-danger',
};

export const DocumentFiles = React.forwardRef(
  (
    props: DocumentFilesProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ): React.ReactElement => {
    const { value, onChange, loading, error, accept } = props;

    const {
      change,
      closeModal,
      openAddModal,
      openEditModal,
      openDeleteModal,
      updateValueAndCloseModal,
    } = useModalChangeActions<EditableDocumentFile>({
      items: value,
      onChange,
      idField: 'id',
    });

    const columns = React.useMemo<DocumentFileColumn[]>(
      () => getColumns({ openEditModal, openDeleteModal }),
      [openEditModal, openDeleteModal],
    );

    const items = React.useMemo(() => {
      return value?.filter((item) => item.changeType !== ChangeType.Removed);
    }, [value]);

    const availableLanguageOptions = React.useMemo(() => {
      if (change?.type === 'create') {
        return languageOptions.filter(
          (opt) => !items?.some((item) => item.language === opt.value),
        );
      }
      return languageOptions;
    }, [items, change?.type]);

    const renderError = (): React.ReactNode => {
      if (!error) return null;
      if (typeof error === 'string') {
        return <p className={classes.error}>{error}</p>;
      }
      return <div className={classes.error}>{error}</div>;
    };

    const innerRef = React.useRef<HTMLDivElement>();
    React.useImperativeHandle(ref, () =>
      Object.assign(innerRef.current, {
        focus: () => {
          innerRef.current?.scrollIntoView({ behavior: 'smooth' });
        },
      }),
    );

    return (
      <>
        <div ref={innerRef} className="odin-flex odin-flex-col odin-gap-y-6">
          <NewHeader
            title="Files"
            size="md"
            actionsProps={{
              headerActions: null,
              children: (
                <Button
                  icon={PlusIcon}
                  text="Add File"
                  size="sm"
                  onClick={openAddModal}
                />
              ),
            }}
          />
          <Table
            loading={loading}
            columns={columns}
            data={items}
            remote
            disableGlobalFilter
            disableSortBy
          />
          {renderError()}
        </div>
        <AddDocumentFileModal
          isOpen={change?.type === 'create'}
          onCancel={closeModal}
          onConfirm={updateValueAndCloseModal}
          languageOptions={availableLanguageOptions}
          accept={accept}
        />
        <EditDocumentFileModal
          isOpen={change?.type === 'update'}
          onCancel={closeModal}
          onConfirm={updateValueAndCloseModal}
          documentFile={change?.item}
          languageOptions={availableLanguageOptions}
          accept={accept}
        />
        <DeleteDocumentFileModal
          isOpen={change?.type === 'remove'}
          onCancel={closeModal}
          onConfirm={updateValueAndCloseModal}
          documentFile={change?.item}
        />
      </>
    );
  },
);
DocumentFiles.displayName = 'DocumentFiles';
